import React, { useState } from 'react';
import { AlertTriangle, Zap, Eye, Check, Calendar, TrendingUp, TrendingDown, Users, Clock, UserPlus, CalendarOff, Mail } from 'lucide-react';
import { PEOPLE_BRIEFING } from '../../data/peopleBriefing';
import { AGENT_THUMBS, SectionLabel, CompactCard, MeetingItem, ShouldDoItem, ExpandedDetails, UrgencyBadge, InboxItem } from './V4BriefingShared';

const CATEGORY_STYLES = {
  capacity: { label: 'Capacity', dark: 'text-red-400 bg-red-400/10', light: 'text-red-600 bg-red-600/10' },
  scheduling: { label: 'Scheduling', dark: 'text-amber-400 bg-amber-400/10', light: 'text-amber-700 bg-amber-700/10' },
  onboarding: { label: 'Onboarding', dark: 'text-emerald-400 bg-emerald-400/10', light: 'text-emerald-600 bg-emerald-600/10' },
  hiring: { label: 'Hiring', dark: 'text-indigo-400 bg-indigo-400/10', light: 'text-indigo-600 bg-indigo-600/10' },
};

const PeopleCard = ({ item, isDark }) => {
  const [expanded, setExpanded] = useState(false);
  const hasAlert = item.alert;
  const cardBg = hasAlert
    ? (isDark ? 'bg-red-950/20' : 'bg-red-50/60')
    : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const border = hasAlert
    ? (isDark ? 'border-red-500/15' : 'border-red-200/60')
    : (isDark ? 'border-white/[0.06]' : 'border-black/[0.06]');
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const hoverBg = isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-black/[0.02]';
  const category = CATEGORY_STYLES[item.category];
  const agentThumb = AGENT_THUMBS[item.agent];

  return (
    <div className={`${cardBg} border ${border} rounded-lg px-3 py-2.5`} data-testid={`people-card-${item.id}`}>
      <div className="flex items-center gap-2">
        {agentThumb && <img src={agentThumb} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />}
        <p className={`text-xs font-semibold flex-1 min-w-0 truncate ${textPrimary}`}>{item.title}</p>
        {category && (
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${isDark ? category.dark : category.light}`}>
            {category.label}
          </span>
        )}
      </div>

      <p className={`text-[11px] mt-1 ml-7 ${textSecondary}`}>{item.description}</p>

      <div className="flex items-center gap-1.5 mt-2 ml-7">
        {hasAlert && item.expanded?.proposedActions && (
          <button className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium ${isDark ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-500/15 text-indigo-600'}`} data-testid={`action-${item.id}`}>
            <Zap className="w-2.5 h-2.5" /> Resolve
          </button>
        )}
        {item.expanded && (
          <button
            onClick={() => setExpanded(!expanded)}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${border} ${textSecondary} ${hoverBg}`}
            data-testid={`review-people-${item.id}`}
          >
            <Eye className="w-2.5 h-2.5" /> {expanded ? 'Less' : 'More'}
          </button>
        )}
      </div>

      {expanded && item.expanded && <ExpandedDetails item={item} isDark={isDark} />}
    </div>
  );
};

const MetricSnapshotRow = ({ metric, isDark }) => {
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const isPositive = metric.trend?.startsWith('+');
  const isNegative = metric.trend?.startsWith('-');
  const trendColor = metric.alert
    ? (isDark ? 'text-red-400' : 'text-red-500')
    : isPositive
      ? (isDark ? 'text-emerald-400' : 'text-emerald-600')
      : isNegative
        ? (isDark ? 'text-red-400' : 'text-red-500')
        : textSecondary;

  return (
    <div className="flex items-center justify-between px-2.5 py-2">
      <span className={`text-xs font-semibold ${textPrimary}`}>{metric.label}</span>
      <div className="flex items-center gap-1.5">
        <span className={`text-xs font-medium ${textPrimary}`}>{metric.value}</span>
        {metric.trend && (
          <span className={`flex items-center gap-0.5 text-[11px] ${trendColor}`}>
            {isPositive ? <TrendingUp className="w-3 h-3" /> : isNegative ? <TrendingDown className="w-3 h-3" /> : null}
            {metric.trend}
          </span>
        )}
      </div>
    </div>
  );
};

const TaskCard = ({ item, isDark }) => {
  const [expanded, setExpanded] = useState(false);
  const cardBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const hoverBg = isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-black/[0.02]';
  const agentThumb = AGENT_THUMBS[item.agent];

  return (
    <div className={`${cardBg} border ${border} rounded-lg px-3 py-2.5`} data-testid={`task-card-${item.id}`}>
      <div className="flex items-center gap-2">
        {agentThumb && <img src={agentThumb} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />}
        <p className={`text-xs font-semibold flex-1 min-w-0 truncate ${textPrimary}`}>{item.title}</p>
        {item.urgency && <UrgencyBadge urgency={item.urgency} isDark={isDark} />}
      </div>
      <p className={`text-[11px] mt-1 line-clamp-1 ml-7 ${textSecondary}`}>{item.description}</p>
      <div className="flex items-center gap-1.5 mt-2 ml-7">
        <button className="flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/20 text-emerald-400 text-[10px] font-medium" data-testid={`review-task-${item.id}`}>
          <Check className="w-2.5 h-2.5" /> Review
        </button>
        {item.expanded && (
          <button
            onClick={() => setExpanded(!expanded)}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${border} ${textSecondary} ${hoverBg}`}
            data-testid={`more-task-${item.id}`}
          >
            <Eye className="w-2.5 h-2.5" /> {expanded ? 'Less' : 'More'}
          </button>
        )}
      </div>
      {expanded && item.expanded && <ExpandedDetails item={item} isDark={isDark} />}
    </div>
  );
};

const V4PeopleBriefing = ({ isDark }) => {
  const data = PEOPLE_BRIEFING;
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';

  return (
    <div className="flex flex-col h-full px-5 py-4" data-testid="people-briefing">
      {/* Greeting from Luc */}
      <div className="mb-4 flex-shrink-0">
        <div className="flex gap-3">
          <img src={AGENT_THUMBS.luc} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5" />
          <div>
            <p className={`text-[15px] ${isDark ? 'text-white/70' : 'text-slate-700'} leading-relaxed`}>
              {data.greeting.lucMessage}
            </p>
            <div className="flex items-center gap-3 mt-1.5">
              <span className={`text-[11px] flex items-center gap-1 ${textSecondary}`}>
                <Calendar className="w-3 h-3" /> {data.greeting.meetings} meetings
              </span>
              <span className={`text-[11px] flex items-center gap-1 ${textSecondary}`}>
                <Check className="w-3 h-3" /> {data.greeting.tasks} tasks
              </span>
              {data.greeting.peopleAlerts > 0 && (
                <span className={`text-[11px] flex items-center gap-1 ${isDark ? 'text-red-400' : 'text-red-500'}`}>
                  <AlertTriangle className="w-3 h-3" /> {data.greeting.peopleAlerts} people alerts
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 3-column grid */}
      <div className="grid grid-cols-3 gap-5 flex-1 min-h-0">
        {/* Column 1: Inbox + Meetings */}
        <div className="flex flex-col min-h-0 overflow-y-auto pr-1">
          {data.inbox && data.inbox.length > 0 && (
            <>
              <SectionLabel icon={Mail} title="Inbox Snapshot" count={data.inbox.length} isDark={isDark} />
              <div className="flex flex-col gap-2 max-h-[280px] overflow-y-auto pr-0.5">
                {data.inbox.map((email) => (
                  <InboxItem key={email.id} email={email} isDark={isDark} />
                ))}
              </div>
            </>
          )}

          <div className="pt-4">
            <SectionLabel icon={Calendar} title="Meetings" count={data.meetings.length} isDark={isDark} />
            <div className="flex flex-col gap-2">
              {data.meetings.map((m) => (
                <MeetingItem key={m.id} meeting={m} isDark={isDark} />
              ))}
            </div>
          </div>
        </div>

        {/* Column 2: Tasks + Burnout Alert */}
        <div className="flex flex-col min-h-0 overflow-y-auto pr-1">
          <SectionLabel icon={Zap} title="Today's Tasks" count={data.tasks.today.length} isDark={isDark} accent="amber" />
          <div className="flex flex-col gap-3">
            {data.tasks.today.map((item) => (
              <TaskCard key={item.id} item={item} isDark={isDark} />
            ))}
          </div>

          {data.peopleMetrics.alert && (
            <div className="pt-4">
              <SectionLabel icon={AlertTriangle} title="Burnout Risk" isDark={isDark} accent="red" />
              <CompactCard item={data.peopleMetrics.alert} isDark={isDark} type="risk" />
            </div>
          )}
        </div>

        {/* Column 3: People Items + People Metrics */}
        <div className="flex flex-col min-h-0 pr-1">
          <SectionLabel icon={Users} title="People & Team" count={data.peopleItems.length} isDark={isDark} accent="amber" />
          <div className="flex flex-col gap-3 max-h-[300px] overflow-y-auto pr-0.5">
            {data.peopleItems.map((item) => (
              <PeopleCard key={item.id} item={item} isDark={isDark} />
            ))}
          </div>

          <div className="pt-4">
            <SectionLabel icon={TrendingUp} title="People Metrics" isDark={isDark} accent="emerald" />
            <div className={`rounded-lg border ${border} overflow-hidden ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`}>
              {data.peopleMetrics.snapshot.map((m, i) => (
                <div key={i} className={i % 2 === 0 ? (isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]') : ''}>
                  <MetricSnapshotRow metric={m} isDark={isDark} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default V4PeopleBriefing;
